import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Code,
  Link,
  Badge,
  Icon,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Tooltip,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react'
import {
  Shield,
  Key,
  Terminal,
  Copy,
  Link2,
  CheckCircle,
  FileText,
  ArrowLeft,
} from 'lucide-react'

const SECTION_IDS = [
  'authentication',
  'endpoints',
  'examples',
  'errors',
]

function CodeBlock({ children, title }: { children: string; title?: string }) {
  const bg = useColorModeValue('stone.100', 'stone.800')
  const headerBg = useColorModeValue('stone.200', 'stone.700')
  const headerColor = useColorModeValue('stone.600', 'stone.300')
  const codeColor = useColorModeValue('stone.700', 'green.300')
  const copyColor = useColorModeValue('stone.500', 'stone.400')
  const copyHoverColor = useColorModeValue('stone.700', 'white')

  return (
    <Box borderRadius="lg" overflow="hidden" my={4} border="1px solid" borderColor={useColorModeValue('stone.200', 'transparent')}>
      {title && (
        <HStack
          bg={headerBg}
          px={4}
          py={2}
          justify="space-between"
        >
          <Text fontSize="sm" color={headerColor} fontFamily="mono">
            {title}
          </Text>
          <Icon
            as={Copy}
            boxSize={4}
            color={copyColor}
            cursor="pointer"
            _hover={{ color: copyHoverColor }}
            onClick={() => navigator.clipboard.writeText(children)}
          />
        </HStack>
      )}
      <Box
        bg={bg}
        p={4}
        overflowX="auto"
      >
        <Code
          display="block"
          whiteSpace="pre"
          bg="transparent"
          color={codeColor}
          fontSize="sm"
          fontFamily="mono"
        >
          {children}
        </Code>
      </Box>
    </Box>
  )
}

function SectionHeader({
  icon,
  title,
  sectionId,
}: {
  icon: React.ElementType
  title: string
  sectionId: string
}) {
  const toast = useToast()
  const linkColor = useColorModeValue('stone.400', 'stone.500')
  const linkHoverColor = useColorModeValue('brand.500', 'brand.400')

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top',
    })
  }

  return (
    <HStack role="group">
      <Icon as={icon} boxSize={5} color="brand.500" />
      <Heading size="md">{title}</Heading>
      <Tooltip label="Copy link to section" placement="top" hasArrow>
        <Box
          as="button"
          onClick={copyLink}
          p={1}
          borderRadius="md"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.2s"
          color={linkColor}
          _hover={{ color: linkHoverColor }}
        >
          <Icon as={Link2} boxSize={4} />
        </Box>
      </Tooltip>
    </HStack>
  )
}

export function ApiDocsPage() {
  const location = useLocation()
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [, setActiveSection] = useState<string | null>(null)
  const isScrollingToHash = useRef(false)
  const subtleColor = useColorModeValue('stone.500', 'stone.400')
  const bodyColor = useColorModeValue('stone.600', 'stone.300')
  const tableBorderColor = useColorModeValue('stone.200', 'stone.600')

  useEffect(() => {
    if (location.hash) {
      isScrollingToHash.current = true
      const element = document.getElementById(location.hash.slice(1))
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
          setTimeout(() => {
            isScrollingToHash.current = false
          }, 1000)
        }, 100)
      }
    }
  }, [])

  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (isScrollingToHash.current) return

      const visibleEntries = entries.filter(entry => entry.isIntersecting)
      if (visibleEntries.length === 0) return

      visibleEntries.sort((a, b) => {
        return a.boundingClientRect.top - b.boundingClientRect.top
      })

      const topSection = visibleEntries[0]
      if (topSection) {
        const sectionId = topSection.target.id
        setActiveSection(sectionId)

        const newUrl = `${location.pathname}#${sectionId}`
        if (window.location.hash !== `#${sectionId}`) {
          window.history.replaceState(null, '', newUrl)
        }
      }
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    SECTION_IDS.forEach(id => {
      const element = document.getElementById(id)
      if (element) {
        observerRef.current?.observe(element)
      }
    })

    return () => {
      observerRef.current?.disconnect()
    }
  }, [location.pathname])

  const baseUrl = `${window.location.origin}/api`

  const createShareExample = `curl -X POST ${baseUrl}/shares \\
  -H "Content-Type: application/json" \\
  -H "X-Myrkup-API-Key: mk_your-secret-key" \\
  -d '{
    "content": "# Hello World\\n\\nThis is my markdown content.",
    "filename": "hello.md",
    "expiresIn": "7d",
    "folder_path": "/myproject/docs",
    "tags": "example,docs"
  }'`

  const createShareResponse = `{
  "shareId": "abc123xyz",
  "managementToken": "...",
  "expiresAt": 1739275200000,
  "filename": "hello.md",
  "format": "md"
}`

  const getMetaExample = `curl ${baseUrl}/shares/abc123xyz/meta \\
  -H "X-Myrkup-API-Key: mk_your-secret-key"`

  const getRawExample = `# With passcode (via header)
curl ${baseUrl.replace('/api', '')}/raw/abc123xyz \\
  -H "X-Myrkup-Passcode: xxxx-xxxx-xxxx"

# Or with API key (owner access)
curl ${baseUrl.replace('/api', '')}/raw/abc123xyz \\
  -H "X-Myrkup-API-Key: mk_your-secret-key"`

  return (
    <VStack spacing={8} align="stretch" maxW="4xl" mx="auto">
      {/* Header */}
      <Box py={4}>
        <Link
          as={RouterLink}
          to="/docs"
          color={subtleColor}
          fontSize="sm"
          display="inline-flex"
          alignItems="center"
          mb={4}
          _hover={{ color: 'brand.500' }}
        >
          <Icon as={ArrowLeft} boxSize={4} mr={1} />
          Back to Docs
        </Link>
        <Badge colorScheme="brand" mb={3} fontSize="sm" display="block" mx="auto" w="fit-content" textAlign="center">
          API Reference
        </Badge>
        <Heading as="h1" size="xl" fontFamily="heading" mb={3} textAlign="center">
          REST API
        </Heading>
        <Text color={subtleColor} fontSize="lg" maxW="2xl" mx="auto" textAlign="center">
          Programmatic access to create, view, and manage encrypted shares.
        </Text>
      </Box>

      {/* Authentication */}
      <Card scrollMarginTop="72px" id="authentication" role="group">
        <CardHeader>
          <SectionHeader icon={Key} title="Authentication" sectionId="authentication" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <Text color={bodyColor}>
              All API requests require authentication using your secret key. Include it in the <Code>Authorization</Code> header:
            </Text>
            <CodeBlock title="Header">{`Authorization: Bearer mk_your-secret-key`}</CodeBlock>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Get your API key from the{' '}
                <Link as={RouterLink} to="/editor?auth=signup" color="brand.500" fontWeight="medium">
                  editor
                </Link>
                . Keys start with <Code>mk_</Code> and are shown only once.
              </Text>
            </Alert>
          </VStack>
        </CardBody>
      </Card>

      {/* Endpoints */}
      <Card scrollMarginTop="72px" id="endpoints" role="group">
        <CardHeader>
          <SectionHeader icon={Terminal} title="Endpoints" sectionId="endpoints" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={6} align="stretch">
            <Text color={bodyColor}>
              Base URL: <Code>{baseUrl}</Code>
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor={tableBorderColor}>Method</Th>
                    <Th borderColor={tableBorderColor}>Endpoint</Th>
                    <Th borderColor={tableBorderColor}>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="green">POST</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/shares</Code></Td>
                    <Td borderColor={tableBorderColor}>Create a new encrypted share</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="blue">GET</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/shares/:id/meta</Code></Td>
                    <Td borderColor={tableBorderColor}>Get share metadata</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="blue">GET</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/shares/:id/blob</Code></Td>
                    <Td borderColor={tableBorderColor}>Get encrypted content blob</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="yellow">PATCH</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/shares/:id/meta</Code></Td>
                    <Td borderColor={tableBorderColor}>Update folder/tags (owner only)</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="red">DELETE</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/shares/:id</Code></Td>
                    <Td borderColor={tableBorderColor}>Delete a share (owner only)</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Badge colorScheme="blue">GET</Badge></Td>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">/raw/:id</Code></Td>
                    <Td borderColor={tableBorderColor}>Get decrypted raw markdown</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                All <Code fontSize="xs">:id</Code> parameters accept an optional file extension (e.g., <Code fontSize="xs">/shares/abc123.md/meta</Code>).
                If provided, it must match the share's filename extension.
              </Text>
            </Alert>

            <Box>
              <Text fontWeight="medium" mb={2}>Create Share Parameters</Text>
              <List spacing={2}>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">content</Code> (required): Markdown content to share
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">filename</Code>: Name for the share (e.g., "readme.md")
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">expiresIn</Code>: "1h", "12h", "1d", "3d", "7d" (Free max), "30d" (Starter max), "90d", "180d", "1y", "never" (Pro only)
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">folder_path</Code>: Virtual folder (e.g., "/project/docs")
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">tags</Code>: Comma-separated tags for filtering (e.g., "draft,docs")
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">burnAfterRead</Code>: Self-destruct after first view (boolean)
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">isModifiable</Code>: Allow content updates via edit link (boolean)
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">format</Code>: Content format - "md" or "mdc"
                  </Text>
                </ListItem>
              </List>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Examples */}
      <Card scrollMarginTop="72px" id="examples" role="group">
        <CardHeader>
          <SectionHeader icon={FileText} title="Examples" sectionId="examples" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontWeight="medium" mb={2}>Create a Share</Text>
              <CodeBlock title="Request">{createShareExample}</CodeBlock>
              <CodeBlock title="Response">{createShareResponse}</CodeBlock>
            </Box>

            <Box>
              <Text fontWeight="medium" mb={2}>Get Share Metadata</Text>
              <Text fontSize="sm" color={bodyColor} mb={2}>
                With your API key, you can access metadata for your own shares:
              </Text>
              <CodeBlock title="Request">{getMetaExample}</CodeBlock>
            </Box>

            <Box>
              <Text fontWeight="medium" mb={2}>Get Raw Markdown</Text>
              <Text fontSize="sm" color={bodyColor} mb={2}>
                The raw endpoint returns decrypted markdown as plain text. Authenticate via header:
              </Text>
              <CodeBlock title="Request">{getRawExample}</CodeBlock>
              <Alert status="info" borderRadius="md" mt={2}>
                <AlertIcon />
                <Text fontSize="sm">
                  Use <Code fontSize="xs">X-Myrkup-Passcode</Code> header for encrypted shares, or{' '}
                  <Code fontSize="xs">X-Myrkup-API-Key</Code> for your own shares.
                  The <Code fontSize="xs">.md</Code> extension is optional.
                </Text>
              </Alert>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Errors */}
      <Card scrollMarginTop="72px" id="errors" role="group">
        <CardHeader>
          <SectionHeader icon={Shield} title="Error Responses" sectionId="errors" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <Text color={bodyColor}>
              The API returns standard HTTP status codes with JSON error bodies:
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor={tableBorderColor}>Code</Th>
                    <Th borderColor={tableBorderColor}>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>400</Code></Td>
                    <Td borderColor={tableBorderColor}>Bad request: invalid parameters</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>401</Code></Td>
                    <Td borderColor={tableBorderColor}>Unauthorized: invalid or missing API key</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>403</Code></Td>
                    <Td borderColor={tableBorderColor}>Forbidden: access denied to this resource</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>404</Code></Td>
                    <Td borderColor={tableBorderColor}>Not found: share doesn't exist or expired</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>410</Code></Td>
                    <Td borderColor={tableBorderColor}>Gone: share was burned after read</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code>429</Code></Td>
                    <Td borderColor={tableBorderColor}>Rate limited: too many requests</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>

            <CodeBlock title="Error Response Format">{`{
  "error": "Share not found or expired",
  "code": "NOT_FOUND"
}`}</CodeBlock>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
